/* eslint-disable @typescript-eslint/no-explicit-any */
import { getNodeType, objectIncludes, type Command } from "@tiptap/core"
import { Node as PMNode, type NodeType } from "@tiptap/pm/model"
import type { EditorState } from "@tiptap/pm/state"
import { getListType, type ListType } from "tiptap-extension-flat-list"
import type { ListKind } from "../analytics/types"

// Helpers for working with "normal" block nodes (paragraph, heading, list item),
// specifically, text blocks in group "block".

/**
 * Checks if all "normal" block nodes in the selection have the given block type.
 *
 * This is similar to Tiptap's isNodeActive, but it skips titles and widgets.
 */
export function isBlockTypeActive(
  state: EditorState,
  typeOrName: NodeType | string,
  attributes: Record<string, any> = {},
) {
  const { from, to, empty } = state.selection
  const type = getNodeType(typeOrName, state.schema)

  let hasMatch = false
  let hasNonMatch = false

  state.doc.nodesBetween(from, to, (node) => {
    if (node.isTextblock && node.type.isInGroup("block")) {
      if (node.type === type && objectIncludes(node.attrs, attributes, { strict: false })) {
        hasMatch = true
      } else hasNonMatch = true
    }
  })

  return empty ? hasMatch : hasMatch && !hasNonMatch
}

/**
 * Command that toggles the block type.
 *
 * Same as Tiptap's toggleNode except:
 * - It uses isBlockTypeActive instead of isNodeActive.
 * - We incorporate tiptap-extension-flat-list's custom toggleFlatListItem.
 */
export function toggleBlockType(
  typeOrName: string | NodeType,
  toggleTypeOrName: string | NodeType,
  attributes?: Record<string, any>,
): Command {
  return ({ state, commands }) => {
    const type = getNodeType(typeOrName, state.schema)
    const toggleType = getNodeType(toggleTypeOrName, state.schema)
    // Cp mod: isNodeActive -> isBlockTypeActive
    const isActive = isBlockTypeActive(state, type, attributes)

    let attributesToCopy: Record<string, any> | undefined

    if (state.selection.$anchor.sameParent(state.selection.$head)) {
      // only copy attributes if the selection is pointing to a node of the same type
      attributesToCopy = state.selection.$anchor.parent.attrs
    }

    if (isActive) {
      return commands.setNode(toggleType, attributesToCopy)
    }

    // If the node is not active, we want to set the new node type with the given attributes
    // Copying over the attributes from the current node if the selection is pointing to a node of the same type

    // Cp mod: If type is from tiptap-extension-flat-list, call setFlatListItem instead of setNode.
    // TODO: Export something from tiptap-extension-flat-list that we can use here.
    const listType = getListType({ type } as PMNode)
    if (listType) {
      return commands.setFlatListItem(listType, {
        ...attributesToCopy,
        ...attributes,
      })
    } else {
      return commands.setNode(type, { ...attributesToCopy, ...attributes })
    }
  }
}

/**
 * Command that sets the block type.
 *
 * Same as Tiptap's setNode except:
 * - We incorporate tiptap-extension-flat-list's custom setFlatListItem,
 * which preserves indents when changing between list types.
 */
export function setBlockType(
  typeOrName: string | NodeType,
  attributes?: Record<string, any>,
): Command {
  return ({ state, commands }) => {
    const type = getNodeType(typeOrName, state.schema)
    // Cp mod: If type is from tiptap-extension-flat-list, call setFlatListItem instead of setNode.
    // TODO: Export something from tiptap-extension-flat-list that we can use here.
    const listType = getListType({ type } as PMNode)
    if (listType) {
      return commands.setFlatListItem(listType, attributes)
    } else {
      return commands.setNode(type, attributes)
    }
  }
}

/** The flat-list `ListType` behind each list kind the operation vocabulary names. */
export const LIST_TYPES = {
  bulleted: "unordered",
  numbered: "ordered",
  checklist: "task",
} satisfies Readonly<Record<Exclude<ListKind, "none">, ListType>>
